import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { analytics } from 'services/mixpanel/analytics-config'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import type { PaywallSource } from 'modules/billing/slices/revenue-cat.slice'
import { toast } from 'sonner'
import { CreativeService } from '../../../openapi/services/CreativeService'
import { useCalendarStore } from '../../calendar/store/calendar-store'
import { useCreativeStore } from '../store/creative-store'
import { usePublishStatusStore } from '../store/publish-status-store'
import { CreativeStatus } from '../types'

/**
 * Mirror of the server's early-publish tolerance
 * (PublishLifecycleService.EARLY_PUBLISH_TOLERANCE_MS): a manual publish of a
 * creative whose slot is further away than this is refused server-side with
 * `scheduled_in_future`. Slots at or before it belong to the sweep and are
 * left alone.
 */
export const EARLY_PUBLISH_TOLERANCE_MS = 2 * 60_000

/**
 * The client half of the early-publish contract. Every manual publish path
 * calls this right before the publish request, AFTER its own gates (paywall,
 * content validation, connection) have passed, so a creative still holding a
 * future slot is taken off it first and the row never reads "scheduled for
 * later, published early". A slot that is due or past is not touched: the
 * sweep may already own it, and the server guard does not apply there.
 * Returns false (with a toast) when the unschedule itself failed, so the
 * caller stops instead of hitting the guard.
 */
export async function takeOffScheduleForManualPublish(creativeId: string): Promise<boolean> {
  const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
  const at = creative?.scheduledAt ? new Date(creative.scheduledAt).getTime() : null
  if (!at || Number.isNaN(at) || at <= Date.now() + EARLY_PUBLISH_TOLERANCE_MS) return true
  try {
    await useCalendarStore.getState().unscheduleCreative(creativeId)
    useCreativeStore.getState().updateCreative(creativeId, { scheduledAt: null })
    // The idea store imports this module; load it lazily to keep the graph acyclic.
    const { useIdeaStore } = await import('../store/idea-store')
    useIdeaStore.getState().markUnscheduled(creativeId)
    return true
  } catch (error) {
    errorReportingService.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        feature: 'manual_publish_unschedule',
        creativeId,
      }
    )
    toast.error("Couldn't take this post off its schedule. Try again.")
    return false
  }
}

// ─── Error classification (for analytics on the catch path) ────────────────
//
// Without this, `Social Publish Failed` events that originate from a fetch
// failure carry only `error: 'Load failed'` (Safari) or `'Failed to fetch'`
// (Chrome) and no other structured fields — the server-side instrumentation
// in instagram-publish.service.ts already attaches failure_reason/error_code
// when its own catch block fires. Together the two paths give us full coverage:
//
//   - error.status numeric  → server returned an error response. Use the body
//     message; classify failure_reason by status range.
//   - no error.status       → request never reached or never returned. Mark
//     failure_reason='client_fetch_failed' and http_status='no_response' so we
//     can bucket these in BigQuery and look for matching server-side events.
function classifyPublishError(error: unknown): {
  message: string
  failureReason: 'client_fetch_failed' | 'api_error'
  httpStatus: string
} {
  const err = error as { body?: { message?: string }; message?: string; status?: number }
  const message = err?.body?.message || err?.message || 'Something went wrong'
  if (typeof err?.status === 'number') {
    return { message, failureReason: 'api_error', httpStatus: String(err.status) }
  }
  return { message, failureReason: 'client_fetch_failed', httpStatus: 'no_response' }
}

// ─── Platform configuration (single source of truth) ────────────────────────
//
// Lifted out of usePublishCreative so both the hook (per-card publish) and
// PostPublishProgressDialog (parallel multi-platform publish) call into the
// same metadata-driven logic. Adding a new platform = extend these two maps.

export type SocialPlatform =
  'instagram' | 'facebook' | 'linkedin' | 'pinterest' | 'tiktok' | 'youtube' | 'x'

export interface PlatformPublishConfig {
  label: string
  provider: SocialPlatform
  subscriptionGate: PaywallSource
  featureFlag: string | null
  publish: (id: string) => Promise<unknown>
  isAsync: boolean
}

export const PLATFORM_PUBLISH_CONFIG: Record<SocialPlatform, PlatformPublishConfig> = {
  instagram: {
    label: 'Instagram',
    provider: 'instagram',
    subscriptionGate: 'publish_instagram',
    featureFlag: null,
    publish: id => CreativeService.creativeControllerPublishToInstagramV1(id),
    isAsync: false,
  },
  facebook: {
    label: 'Facebook',
    provider: 'facebook',
    subscriptionGate: 'publish_facebook',
    featureFlag: null,
    publish: id => CreativeService.creativeControllerPublishToFacebookV1(id),
    isAsync: false,
  },
  linkedin: {
    label: 'LinkedIn',
    provider: 'linkedin',
    subscriptionGate: 'publish_linkedin',
    featureFlag: null,
    publish: id => CreativeService.creativeControllerPublishToLinkedInV1(id),
    isAsync: false,
  },
  tiktok: {
    label: 'TikTok',
    provider: 'tiktok',
    subscriptionGate: 'publish_tiktok',
    featureFlag: 'enable_tiktok',
    publish: id => CreativeService.creativeControllerPublishToTikTokV1(id),
    isAsync: true,
  },
  youtube: {
    label: 'YouTube',
    provider: 'youtube',
    subscriptionGate: 'publish_youtube',
    featureFlag: 'enable_youtube',
    publish: id => CreativeService.creativeControllerPublishToYouTubeV1(id),
    isAsync: true,
  },
  pinterest: {
    label: 'Pinterest',
    provider: 'pinterest',
    subscriptionGate: 'publish_pinterest',
    featureFlag: 'enable_pinterest',
    publish: id => CreativeService.creativeControllerPublishToPinterestV1(id),
    // Async — video pins take 1–3 min (Pinterest media processing); the
    // sync HTTP path was hitting the LB's response timeout and the browser
    // showed "Failed to fetch" even though the pin published successfully.
    // Server now responds 202 immediately and fires Pusher on completion.
    isAsync: true,
  },
  x: {
    label: 'X',
    provider: 'x',
    // Same gate the composer's own "Post to X" button checks
    // (ComposerHeader → usePlatformPublish provider 'twitter').
    subscriptionGate: 'publish_twitter',
    featureFlag: 'enable_x',
    publish: id => CreativeService.creativeControllerPublishToXV1(id),
    // Async — `POST /creatives/:id/publish-x` returns 202 and
    // XPublishService.publishInBackground lands the outcome via Pusher
    // (CreativePublishStarted / Complete with platform 'x'). Missing here
    // until 2026-09-06, which left PostModal's Post-now and Retry unable to
    // publish an X row at all (livePlatformToSocial returned null).
    isAsync: true,
  },
}

export const PUBLISH_METADATA_KEY: Record<SocialPlatform, string> = {
  instagram: 'instagramPublish',
  facebook: 'facebookPublish',
  linkedin: 'linkedinPublish',
  tiktok: 'tiktokPublish',
  youtube: 'youtubePublish',
  pinterest: 'pinterestPublish',
  x: 'xPublish',
}

/**
 * Analytics platform key. The X integration is provider `twitter` on the
 * server and every server-side X event (`SOCIAL_PUBLISH_*`) plus the
 * composer's tap event use `twitter`; keep the client-side funnel events on
 * the same key so BigQuery joins line up.
 */
export function analyticsPlatformFor(channel: SocialPlatform): string {
  return channel === 'x' ? 'twitter' : channel
}

export function isSocialPlatform(channel: string): channel is SocialPlatform {
  return channel in PLATFORM_PUBLISH_CONFIG
}

/**
 * The home grid uses `LivePlatform` keys ('youtube_shorts'), but the publish
 * config + OAuth provider key use 'youtube'. Bridge the two so `PostModal` and
 * `PostPublishProgressDialog` can drive publishes off `LivePlatform` directly.
 * Returns null for non-publishable channels.
 */
export function livePlatformToSocial(platform: string): SocialPlatform | null {
  if (platform === 'youtube_shorts') return 'youtube'
  if (isSocialPlatform(platform)) return platform
  return null
}

/**
 * Resolve "should we fake the publish?" by combining the PostHog
 * `simulate_publish` flag with a localStorage override for dev/QA.
 *
 * To flip on for testing without touching PostHog, run in the browser console:
 *   localStorage.setItem('simulate_publish', 'true')
 * …then refresh. To turn off:
 *   localStorage.removeItem('simulate_publish')
 *
 * The override is a one-way OR with the gate — anyone in the gated cohort
 * still gets the simulate path even if they haven't set the local key.
 */
export function resolveSimulatePublish(gateValue: boolean): boolean {
  if (gateValue) return true
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem('simulate_publish') === 'true'
}

// ─── Publish a single creative to its target channel ────────────────────────

/**
 * Sync result returned immediately from the API call (Instagram, Facebook,
 * LinkedIn, Pinterest). Async result is just an acknowledgement — the real
 * outcome arrives via Pusher → `usePublishStatusStore`.
 */
export type PublishCreativeResult =
  | {
      kind: 'sync'
      success: boolean
      permalink?: string
      publishId?: string
      error?: string
      retryable?: boolean
    }
  | { kind: 'async-pending' }

interface PublishCreativeArgs {
  creativeId: string
  channelType: SocialPlatform
  /** Source label for analytics (e.g. 'post-modal', 'composer'). */
  source: string
  /** When true, the publish is faked locally — no server call, no OAuth. */
  simulate: boolean
}

/**
 * Stateless publish-once for a single creative to a single platform. Reused by
 * both `usePublishCreative` (per-card) and `PostPublishProgressDialog` (multi-
 * platform parallel). Mirrors `usePublishCreative.executePublish` but returns
 * the outcome instead of pushing to local React state.
 *
 * Side effects on success: writes `CreativeStatus.Published` + per-platform
 * publish metadata (`{platform}Publish.permalink/lastPublishedAt/...`) into
 * `useCreativeStore`. For async platforms, also flips the per-creative entry
 * in `usePublishStatusStore` to `'publishing'` so consumers can render their
 * own spinners while waiting on Pusher.
 */
export async function publishCreativeOnce(
  args: PublishCreativeArgs
): Promise<PublishCreativeResult> {
  const { creativeId, channelType, source, simulate } = args
  const config = PLATFORM_PUBLISH_CONFIG[channelType]

  const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
  const content = creative?.content as Record<string, unknown> | undefined
  const contentType = content?.videoUrl || content?.videoAttachmentId ? 'video' : 'image'

  const analyticsProps = {
    platform: analyticsPlatformFor(config.provider),
    creative_id: creativeId,
    source,
    content_type: contentType,
  }

  analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TRIGGERED, analyticsProps)
  errorReportingService.addBreadcrumb(`publish_triggered: ${config.provider}`, 'publish', {
    creativeId,
    channelType,
    source,
    contentType,
  })

  // ── Simulate path ───────────────────────────────────────────────────────
  if (simulate) {
    const fakePermalink = `https://www.${channelType}.com/p/fake-${creativeId.slice(0, 8)}/`
    const fakeMeta: Record<string, unknown> = {
      lastPublishedAt: new Date().toISOString(),
      permalink: fakePermalink,
    }
    if (channelType === 'instagram') {
      fakeMeta.mediaId = `fake-media-${Date.now()}`
      fakeMeta.contentHash = ''
    } else if (channelType === 'facebook') {
      fakeMeta.postId = `fake-post-${Date.now()}`
      fakeMeta.contentHash = ''
    } else if (channelType === 'linkedin') {
      fakeMeta.postUrn = `urn:li:share:fake-${Date.now()}`
    } else if (channelType === 'pinterest') {
      fakeMeta.pinId = `fake-pin-${Date.now()}`
      fakeMeta.contentHash = ''
    } else if (channelType === 'tiktok') {
      fakeMeta.publishId = `fake-tiktok-${Date.now()}`
    } else if (channelType === 'youtube') {
      fakeMeta.videoId = `fake-yt-${Date.now()}`
    } else if (channelType === 'x') {
      fakeMeta.tweetId = `fake-tweet-${Date.now()}`
      fakeMeta.contentHash = ''
    }

    const store = useCreativeStore.getState()
    store.updateCreative(creativeId, { status: CreativeStatus.Published })
    store.updateCreativeContent(
      creativeId,
      { [PUBLISH_METADATA_KEY[channelType]]: fakeMeta },
      { userEdit: false }
    )

    analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_SUCCESS, analyticsProps)
    return { kind: 'sync', success: true, permalink: fakePermalink }
  }

  // A creative still holding a future slot comes off it first (the server
  // refuses the publish otherwise); a failed unschedule reads as a failed publish.
  if (!(await takeOffScheduleForManualPublish(creativeId))) {
    return {
      kind: 'sync',
      success: false,
      error: 'Could not take the post off its schedule.',
      retryable: true,
    }
  }

  // ── Async path — fire and let Pusher land the result ────────────────────
  if (config.isAsync) {
    usePublishStatusStore.getState().setPublishing(creativeId, channelType)
    try {
      await config.publish(creativeId)
      return { kind: 'async-pending' }
    } catch (error) {
      const { message, failureReason, httpStatus } = classifyPublishError(error)
      usePublishStatusStore.getState().setComplete(creativeId, {
        platform: channelType,
        success: false,
        error: message,
        retryable: true,
      })
      analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_FAILED, {
        ...analyticsProps,
        error: message,
        failure_reason: failureReason,
        http_status: httpStatus,
      })
      errorReportingService.captureException(error instanceof Error ? error : new Error(message), {
        feature: `${channelType}_publish`,
        creativeId,
      })
      return { kind: 'sync', success: false, error: message, retryable: true }
    }
  }

  // ── Sync path ───────────────────────────────────────────────────────────
  try {
    const result = (await config.publish(creativeId)) as {
      success: boolean
      permalink?: string
      mediaId?: string
      postId?: string
      postUrn?: string
      pinId?: string
      contentHash?: string
      error?: string
      retryable?: boolean
    }

    if (result.success) {
      const meta: Record<string, unknown> = {
        lastPublishedAt: new Date().toISOString(),
        permalink: result.permalink,
        contentHash: result.contentHash ?? '',
      }
      if (result.mediaId) meta.mediaId = result.mediaId
      if (result.postId) meta.postId = result.postId
      if (result.postUrn) meta.postUrn = result.postUrn
      if (result.pinId) meta.pinId = result.pinId

      const store = useCreativeStore.getState()
      store.updateCreative(creativeId, { status: CreativeStatus.Published })
      store.updateCreativeContent(
        creativeId,
        { [PUBLISH_METADATA_KEY[channelType]]: meta },
        { userEdit: false }
      )

      analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_SUCCESS, analyticsProps)
      return {
        kind: 'sync',
        success: true,
        permalink: result.permalink,
        publishId: result.mediaId || result.postId || result.postUrn || result.pinId,
      }
    }

    const msg = result.error || 'Publishing failed'
    // Server returned a structured 200 with success:false (the InstagramApiError
    // path returns this shape). The server-side service has already tracked
    // SOCIAL_PUBLISH_FAILED with the rich failure_reason/error_code; this
    // client-side track captures the user-side completion. Classify as
    // api_error so it doesn't pollute the client_fetch_failed bucket we use
    // for diagnosing unreachable-server failures.
    analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_FAILED, {
      ...analyticsProps,
      error: msg,
      failure_reason: 'api_error',
      http_status: '200',
    })
    return { kind: 'sync', success: false, error: msg, retryable: result.retryable }
  } catch (error) {
    const { message, failureReason, httpStatus } = classifyPublishError(error)
    analytics.track(AnalyticsEvents.Social.SOCIAL_PUBLISH_FAILED, {
      ...analyticsProps,
      error: message,
      failure_reason: failureReason,
      http_status: httpStatus,
    })
    errorReportingService.captureException(error instanceof Error ? error : new Error(message), {
      feature: `${channelType}_publish`,
      creativeId,
    })
    return { kind: 'sync', success: false, error: message, retryable: true }
  }
}
